import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DEFAULT_MAP_CENTER,
  LOCATION_NOTE_MAX,
  OSM_ATTRIBUTION,
  OSM_TILE_URL,
  hasExactLocation,
  truckPinIcon,
} from '../lib/truckLocation';

function MapClickToPin({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function FollowPin({ position }) {
  const map = useMap();
  const [lat, lng] = position || [];
  useEffect(() => {
    if (lat == null || lng == null) return;
    map.setView([lat, lng], Math.max(map.getZoom(), 17));
  }, [map, lat, lng]);
  return null;
}

/**
 * value: { lat, lng, note } | null
 * onChange(nextValue | null)
 */
export default function TruckLocationPicker({ value, onChange, disabled = false }) {
  const [locating, setLocating] = useState(false);
  const pinned = hasExactLocation(value);
  const position = pinned ? [Number(value.lat), Number(value.lng)] : null;

  const setPin = (lat, lng) => {
    if (disabled) return;
    onChange({ lat, lng, note: value?.note || '' });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location is not available on this device. Tap the map to drop the pin instead.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setPin(pos.coords.latitude, pos.coords.longitude);
        if (pos.coords.accuracy > 50) {
          toast.message(`GPS is accurate to about ${Math.round(pos.coords.accuracy)} m — drag the pin to fine-tune.`);
        }
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error('Location permission denied. Allow location access, or tap the map to drop the pin.');
        } else {
          toast.error('Could not get your location. Tap the map to drop the pin instead.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={useCurrentLocation}
          disabled={disabled || locating}
          data-testid="use-current-location"
        >
          {locating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Crosshair className="w-4 h-4 mr-1" />}
          Use my current location
        </Button>
        {pinned && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            className="text-destructive hover:text-destructive"
            data-testid="remove-location-pin"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Remove pin
          </Button>
        )}
      </div>

      <div className="relative isolate z-0 h-64 rounded-lg overflow-hidden border">
        <MapContainer
          center={position || DEFAULT_MAP_CENTER}
          zoom={position ? 17 : 15}
          scrollWheelZoom={false}
          className="h-full w-full"
        >
          <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} />
          <MapClickToPin onPick={setPin} />
          {position && (
            <>
              <Marker
                position={position}
                icon={truckPinIcon}
                draggable={!disabled}
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    setPin(lat, lng);
                  },
                }}
              />
              <FollowPin position={position} />
            </>
          )}
        </MapContainer>
      </div>
      <p className="text-xs text-muted-foreground">
        {pinned
          ? 'Drag the pin or tap the map to move it.'
          : 'Use your current location, or tap the map where the truck is parked.'}
      </p>

      <div>
        <Input
          value={value?.note || ''}
          onChange={(e) => {
            const note = e.target.value.slice(0, LOCATION_NOTE_MAX);
            if (pinned) onChange({ ...value, note });
          }}
          placeholder={pinned ? 'e.g., Corner of St. George & Harbord, by the bike racks' : 'Drop a pin first to add a note'}
          disabled={disabled || !pinned}
          data-testid="location-note"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Optional note for students ({(value?.note || '').length}/{LOCATION_NOTE_MAX})
        </p>
      </div>
    </div>
  );
}
